import { COUNTRIES } from "../config/countries.config";
import type { PayoutRail } from "../services/paymentsPayout.service";
import type { EnhancedCountry } from "../types/dispersions-api.types";

export const DISPERSIONS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Corridors executed by the NEW NestJS payments runtime (Brazil PIX, Mexico SPEI, Argentina CBU/CVU, Colombia
 * Bre-B). The beneficiary is always paid in the corridor currency (BRL/MXN/ARS/COP), but the wallet debited
 * no longer has to match it: when they differ, the runtime converts inside the payout at a frozen quote rate.
 * The balance shown and validated in the form is therefore the runtime wallet of the ORIGIN currency.
 */
export const RUNTIME_CORRIDOR_ISO3 = ["BRA", "MEX", "ARG", "COL"] as const;

export type RuntimeCorridor = (typeof RUNTIME_CORRIDOR_ISO3)[number];

/**
 * Currency each corridor delivers in. The corridor is the source of truth for it — resolving it from the
 * countries catalog instead would make the quote request depend on the catalog state having been seeded.
 */
export const CURRENCY_BY_RUNTIME_CORRIDOR: Record<RuntimeCorridor, string> = {
  BRA: "BRL",
  MEX: "MXN",
  ARG: "ARS",
  COL: "COP",
};

/** Default rail each corridor runs on, needed by both the quote request and the payout. */
export const RAIL_BY_RUNTIME_CORRIDOR: Record<RuntimeCorridor, PayoutRail> = {
  BRA: "PIX",
  MEX: "SPEI",
  ARG: "CBU_CVU",
  COL: "BREB",
};

/**
 * EVERY payout rail a corridor can run on — what availability checks must probe. Colombia has two; probing
 * only the default (BREB) would hide the country while Transfiya is still alive.
 */
export const RAILS_BY_RUNTIME_CORRIDOR: Record<RuntimeCorridor, readonly PayoutRail[]> = {
  BRA: ["PIX"],
  MEX: ["SPEI"],
  ARG: ["CBU_CVU"],
  COL: ["BREB", "TRANSFIYA"],
};

/**
 * Rail the QUOTE must be frozen for. Colombia is the only corridor with two payout rails (Bre-B default,
 * Transfiya via the step-2 toggle) and the backend refuses to redeem a quote priced for another corridor — so
 * the quote request must follow the selection.
 */
export function railForQuote(
  corridor: RuntimeCorridor,
  recipientFields: Record<string, unknown> | null | undefined,
): PayoutRail {
  if (corridor === "COL") return resolveColRail(recipientFields);
  return RAIL_BY_RUNTIME_CORRIDOR[corridor];
}

/**
 * Which Colombian rail the step-2 toggle currently selects, defaulting to Bre-B.
 *
 * `col_rail` is stored as a loose `unknown` in `recipientFields`, so every reader has to coerce and default
 * it. That was written out by hand in five places — the validation, the submit payload, the confirmation
 * summary (twice) and the toggle itself — which is one spelling away from the validation and the payload
 * disagreeing about which rail is being sent.
 */
export function resolveColRail(
  recipientFields: Record<string, unknown> | null | undefined,
): Extract<PayoutRail, "BREB" | "TRANSFIYA"> {
  return String(recipientFields?.col_rail ?? "BREB") === "TRANSFIYA" ? "TRANSFIYA" : "BREB";
}

// The page historically tolerates several destination spellings (ISO3 or country name) — normalize all.
const RUNTIME_CORRIDOR_ALIASES: Record<string, RuntimeCorridor> = {
  BRA: "BRA",
  BRASIL: "BRA",
  BRAZIL: "BRA",
  MEX: "MEX",
  MÉXICO: "MEX",
  MEXICO: "MEX",
  ARG: "ARG",
  ARGENTINA: "ARG",
  COL: "COL",
  COLOMBIA: "COL",
};

/**
 * Canonical runtime corridor for a country code/name spelling, or null when the corridor is legacy (PHP).
 * SINGLE source of the spelling tolerance — submit/validation must branch through this, never through inline
 * string compares: a missed alias would silently route a runtime corridor to the legacy proxy.
 */
export function resolveRuntimeCorridor(code: string | null | undefined): RuntimeCorridor | null {
  if (!code) return null;
  return RUNTIME_CORRIDOR_ALIASES[code.trim().toUpperCase()] ?? null;
}

/** True when the country code/name belongs to a corridor executed by the payments runtime. */
export function isRuntimeCorridor(code: string | null | undefined): boolean {
  return resolveRuntimeCorridor(code) !== null;
}

/**
 * The wizard's destination catalog: exactly the corridors the payments runtime executes, sourced from the
 * local country config. It used to come from the legacy PHP `GET /utilities/countries`, which coupled the
 * whole wizard to that proxy answering (an error there emptied the selector, runtime corridors included) and
 * whose `payout` flag no longer decides anything — the legacy corridors it gated are retired with the PHP
 * integration. Offering a corridor is a product decision made here, in code, reviewed like any other change.
 */
export const PAYOUT_WIZARD_COUNTRIES: EnhancedCountry[] = RUNTIME_CORRIDOR_ISO3.map((iso3) => {
  const config = Object.values(COUNTRIES).find((c) => c.iso3 === iso3);
  if (!config) throw new Error(`Runtime corridor ${iso3} is missing from the country config`);
  return {
    name: config.name,
    code: config.iso3,
    currency: config.currency,
    currencySymbol: config.currencySymbol,
    flag: config.flag,
    hasPayout: true,
  };
});

// ISO3 to ISO2 mapping for countries not in existing config
export const ISO3_TO_ISO2_MAP: Record<string, string> = {
  COL: "CO",
  BRA: "BR",
  MEX: "MX",
  ARG: "AR",
  CHL: "CL",
  ECU: "EC",
  PAN: "PA",
  PER: "PE",
  PRY: "PY", // Paraguay
  GTM: "GT", // Guatemala
  EUR: "EU", // Unión Europea
  USDT: "XX", // Crypto (placeholder)
  USDC: "XX", // Crypto (placeholder)
  BINANCE_PAY: "XX", // Binance Pay (placeholder)
};

// Currency symbols for new countries
export const CURRENCY_SYMBOLS: Record<string, string> = {
  PYG: "₲",
  GTQ: "Q",
  EUR: "€",
  USDT: "$",
  USDC: "$",
  BINANCE_PAY: "$",
  // Fallback for existing
  COP: "$",
  BRL: "R$",
  MXN: "$",
  ARS: "$",
  CLP: "$",
  USD: "$",
  PEN: "S/",
};
